/**
 * issues.portfolio_overview — workspace issue portfolio with stance and
 * priority. Surfaces the issues bar so the operator can see the
 * full lobbying brief at a glance from the dashboard.
 */
import { useQuery } from "@tanstack/react-query";
import { DataUnavailable } from "../../../framework/react/DataUnavailable.js";
import { Link } from "react-router-dom";
import { WidgetProps } from "../registry";
import { Skeleton } from "../../components/ui";

const STANCE_BG: Record<string, string> = {
  support: "bg-green-100 text-green-800",
  oppose: "bg-red-100 text-red-800",
  monitor: "bg-amber-100 text-amber-800",
  neutral: "bg-stone-100 text-stone-600",
};

export function IssuesPortfolioOverview({ spec, workspaceId }: WidgetProps) {
  const limit = (spec.params?.limit as number) ?? 8;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["widget", "issues.portfolio", workspaceId],
    queryFn: async () => {
      const res = await fetch(`/api/v1/workspaces/${workspaceId}/issues-index`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load");
      return res.json();
    },
  });

  if (error) return <DataUnavailable message="Issues could not be loaded." retry={refetch} />;
  if (isLoading) {
    return (
      <div role="status" className="space-y-2">
        <span className="sr-only">Loading…</span>
        <Skeleton className="h-3 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
        <Skeleton className="h-3 w-2/3" />
      </div>
    );
  }
  const issues: any[] = data?.issues ?? [];
  if (issues.length === 0) {
    return <div className="text-stone-500 text-xs">No issues in portfolio yet.</div>;
  }

  // Sort by priority (assuming numeric/string field) then by name
  const PRIORITY_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
  const sorted = [...issues]
    .sort((a, b) => {
      const ap = PRIORITY_ORDER[a.priority] ?? 99;
      const bp = PRIORITY_ORDER[b.priority] ?? 99;
      if (ap !== bp) return ap - bp;
      return (a.name || "").localeCompare(b.name || "");
    })
    .slice(0, limit);

  return (
    <ul className="space-y-1 text-xs">
      {sorted.map((iss: any) => (
        <li key={iss.id}>
          <Link to={`/issues/${encodeURIComponent(iss.id)}/whip-count`} className="flex items-center gap-2 hover:bg-stone-50 px-1 py-0.5 rounded -mx-1">
            {iss.color && (
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: iss.color }} />
            )}
            <span className="flex-1 truncate text-stone-700">{iss.name}</span>
            {iss.stance && (
              <span className={`tier-tag text-[9px] ${STANCE_BG[iss.stance] || ""}`}>{iss.stance}</span>
            )}
            {iss.bill_count != null && (
              <span className="font-mono text-stone-500 text-[10px]" title={`${iss.bill_count} bills`}>{iss.bill_count}b</span>
            )}
          </Link>
        </li>
      ))}
    </ul>
  );
}
